import React from 'react';
import {
  WhatsAppOutlined,
  ShoppingCartOutlined,
  TruckOutlined,
  BarcodeOutlined,
  ShopOutlined,
  InboxOutlined,
  RobotOutlined,
  NotificationOutlined,
  BarChartOutlined,
  TeamOutlined
} from '@ant-design/icons';

/**
 * Modules présentés sur les pages publiques (accueil, fonctionnalités).
 * Chaque point provient du code ou de la documentation du projet
 * (navigation du backoffice, `documentation/*.md`). Ne pas y ajouter une
 * fonction qui n'existe pas dans l'application.
 *
 * - `resume` : phrase courte (accueil).
 * - `details` : liste détaillée (page Fonctionnalités).
 * - `large` : carte agrandie dans la grille de l'accueil.
 */
export const FEATURE_MODULES = [
  {
    key: 'whatsapp',
    icon: <WhatsAppOutlined />,
    titre: 'WhatsApp pour votre boutique',
    resume: 'Connectez votre fournisseur WhatsApp API et envoyez vos messages depuis une file d\'envoi suivie.',
    details: [
      'Connexion via un fournisseur WhatsApp API (WhatChimp, WATI, WasenderAPI, AiSensy)',
      'File d\'envoi avec statut, tentatives et annulation des messages en attente',
      'Modèles de message personnalisables',
      'Alerte Telegram aux administrateurs en cas de déconnexion de WhatsApp'
    ],
    large: true,
    tags: ['WhatsApp API', 'Modèles', 'File d\'envoi']
  },
  {
    key: 'commandes',
    icon: <ShoppingCartOutlined />,
    titre: 'Commandes',
    resume: 'Création manuelle, depuis la vitrine ou la caisse, avec modification, annulation, remboursement et lien de suivi.',
    details: [
      'Commandes créées à la main, depuis la vitrine en ligne ou depuis la caisse',
      'Modification des articles, annulation, remboursement et marquage « payée »',
      'Lien public de suivi de commande pour le client',
      'Notification WhatsApp de nouvelle commande aux administrateurs',
      'Avis clients après livraison'
    ]
  },
  {
    key: 'livraisons',
    icon: <TruckOutlined />,
    titre: 'Livraisons',
    resume: 'Livreurs, zones, disponibilités, attribution des commandes et message WhatsApp programmé au client.',
    details: [
      'Livreurs avec zones préférentielles et planning de disponibilité',
      'Attribution manuelle ou automatique des commandes',
      'Message de livraison au client : programmé, immédiat ou désactivé',
      'Page de suivi accessible au livreur par lien',
      'Revenus et statistiques par livreur'
    ]
  },
  {
    key: 'pos',
    icon: <BarcodeOutlined />,
    titre: 'Caisse (POS)',
    resume: 'Application de caisse avec code PIN, lecture code-barres, ventes en attente et reçus envoyés sur WhatsApp.',
    details: [
      'Connexion des caissiers par code PIN',
      'Recherche produit par nom ou code-barres, avec variantes',
      'Paiement espèces, carte ou mobile money',
      'Ventes mises en attente puis reprises',
      'Reçus envoyés au client sur WhatsApp',
      'Points de fidélité attribués et utilisés à la caisse'
    ],
    large: true,
    tags: ['Code PIN', 'Code-barres', 'Reçu WhatsApp'],
    plan: 'Croissance'
  },
  {
    key: 'vitrine',
    icon: <ShopOutlined />,
    titre: 'Vitrine en ligne',
    resume: 'Un site par boutique sur son propre sous-domaine, avec catalogue, tunnel de commande et zones de livraison.',
    details: [
      'Site de la boutique sur un sous-domaine dédié',
      'Logo, couleurs et contacts configurables',
      'Catalogue avec recherche et filtres',
      'Tunnel de commande avec zones et jours de livraison',
      'Acompte à la commande et confirmation du client par code reçu sur WhatsApp'
    ]
  },
  {
    key: 'produits',
    icon: <InboxOutlined />,
    titre: 'Produits et stocks',
    resume: 'Catalogue avec variantes et images multiples, codes SKU et code-barres, réductions et alertes de retour en stock.',
    details: [
      'Produits simples ou à variantes (couleur, taille…) avec images multiples',
      'Génération de SKU et de codes-barres',
      'Codes de réduction',
      'Avis sur les produits',
      'Notifications de retour en stock'
    ]
  },
  {
    key: 'ia',
    icon: <RobotOutlined />,
    titre: 'Assistant IA',
    resume: 'Interrogez vos commandes en langage naturel, analysez vos factures et générez images produit et fiches produit.',
    details: [
      'Assistant commercial limité aux données de commandes de votre compte',
      'Analyse IA des factures et reçus ajoutés aux dépenses',
      'Analyse de rentabilité avec recommandations',
      'Fiches produit pré-remplies et images produit générées',
      'Enveloppe mensuelle de points IA selon l\'offre'
    ],
    large: true,
    tags: ['Assistant', 'Factures', 'Images']
  },
  {
    key: 'meta',
    icon: <NotificationOutlined />,
    titre: 'Publicités Meta',
    resume: 'Créez vos campagnes Meta Ads, suivez les prospects et les commandes qui en découlent.',
    details: [
      'Campagnes Meta Ads avec brouillons et carrousels',
      'Textes et visuels d\'annonces générés par IA',
      'Suivi des conversations démarrées et des formulaires de prospects',
      'Pixel et API Conversions Facebook, catalogue produits Facebook'
    ]
  },
  {
    key: 'analyses',
    icon: <BarChartOutlined />,
    titre: 'Analyses et dépenses',
    resume: 'Tableau de bord des ventes, des clients et des livraisons, avec suivi des dépenses.',
    details: [
      'Tableau de bord des commandes et du chiffre d\'affaires',
      'Statistiques clients et produits',
      'Suivi des dépenses avec import de factures',
      'Clients injoignables à relancer'
    ]
  },
  {
    key: 'equipe',
    icon: <TeamOutlined />,
    titre: 'Équipe et boutiques',
    resume: 'Invitez vos collaborateurs avec des rôles, gérez plusieurs boutiques et points de vente.',
    details: [
      'Utilisateurs avec rôles (propriétaire, administrateur, vendeur)',
      'Invitation de collaborateurs par lien',
      'Plusieurs boutiques et points de vente',
      'Données isolées par boutique'
    ]
  }
];
